import { CourseRequirementType, ICourseRequirement, ICreateCourseRequirement } from '@/types/courses';
import { BrandliveEvent } from '@/types/projects.types';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import '../courses.css';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Button from '@ui/buttons/button/button';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { TGlobalContainerDesigns } from '@styles/design-system/global-components/global-components.types';

type Props = {
  requirement: ICourseRequirement;
  workingEvent: BrandliveEvent | null;
  setRequirementToUpdate: (uuid: string) => void;
  setRequirementToDelete: (uuid: string) => void;
  setNewRequirement: (requirement: ICreateCourseRequirement) => void;
  setShowAddNewRequirementModal: (show: boolean) => void;
  i: number;
};
export default function CourseRequirementRow({
  requirement,
  workingEvent,
  setRequirementToUpdate,
  setRequirementToDelete,
  setNewRequirement,
  setShowAddNewRequirementModal,
  i,
}: Props) {
  const name = requirement.type === CourseRequirementType.watched_minute ? 'Watchtime' : 'Pulse check';

  return (
    <Container
      background="var(--gray-900)"
      height="64px"
      key={requirement.uuid}
      className="requirements-item radius-8"
      data-testid={`course-edit-requirement-${i}`}
    >
      <div className="position-absolute width-100-percent height-100-percent container">
        <Container
          className="flex jc-space-between ai-center padding-l-16 padding-r-16 height-100-percent radius-12"
          background={'var(--gray-800)'}
        >
          <Text className="S1 flex gap-10 ai-center" color="var(--gray-100)">
            {name}
            <TooltipPortal
              tooltipContent={
                requirement.type === CourseRequirementType.watched_minute ? (
                  <Text tag="span" color="var(--gray-true-dark-100)">
                    Watch {requirement.required_amount ?? 'x'} minutes to complete this requirement
                  </Text>
                ) : (
                  <Text tag="span" color="var(--gray-true-dark-100)">
                    Demonstrate your engagement via {requirement.required_amount ?? 'x'} check-ins
                  </Text>
                )
              }
            >
              <Icon name={ICONS.INFO_CIRCLE} color="var(--gray-400)" size={12} />
            </TooltipPortal>
          </Text>
          <Container
            design={TGlobalContainerDesigns.GlobalCont4}
            className="padding-6 padding-l-12 padding-r-12 radius-8"
          >
            <Text className="S1" design="GlobalIcoText1">
              {name}
            </Text>
          </Container>
        </Container>
      </div>
      <Container
        className="hover-state opacity-0 flex jc-space-between padding-l-16 height-100-percent radius-8"
        background={'var(--gray-800)'}
      >
        <Container className="flex-column jc-center">
          <Text className="S1 flex gap-10 ai-center" color="var(--gray-100)">
            {name}
            <TooltipPortal
              tooltipContent={
                requirement.type === CourseRequirementType.watched_minute ? (
                  <span>Watch {requirement.required_amount ?? 'x'} minutes to complete this requirement</span>
                ) : (
                  <span>Demonstrate your engagement via {requirement.required_amount ?? 'x'} check-ins</span>
                )
              }
            >
              <Icon name={ICONS.INFO_CIRCLE} color="var(--gray-400)" size={12} />
            </TooltipPortal>
          </Text>
          <div className="flex gap-10">
            <Text className="S2" color="var(--gray-300)">
              {workingEvent?.sessions.find((session) => session.session === requirement.session_id)?.title.base || ''} •
              Pass threshold
            </Text>
            <Text className="S2">
              {requirement.required_amount}
              {requirement.type === CourseRequirementType.watched_minute ? 'm' : ''}
            </Text>
          </div>
        </Container>
        <Container className="flex ai-center gap-10 padding-r-16">
          <div
            data-testid={`course-edit-requirement-${i}-delete-btn`}
            onClick={() => setRequirementToDelete(requirement.uuid)}
          >
            <Button
              className="flex jc-center ai-center radius-full padding-0"
              width="32px"
              height="32px"
              design="LocalBttnSec"
            >
              <Icon name={ICONS.TRASH} size={12} />
            </Button>
          </div>
          <Button
            height="32px"
            width="auto"
            onClick={() => {
              setRequirementToUpdate(requirement.uuid);
              setNewRequirement(requirement);
              setShowAddNewRequirementModal(true);
            }}
            hoverOpacity={0.8}
            backgroundColor={getThemeColorCSSVariable()}
          >
            <Text
              className="L1 flex ai-center gap-6"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              <Icon name={ICONS.EDIT} size={12} />
              <span>Edit</span>
            </Text>
          </Button>
        </Container>
      </Container>
    </Container>
  );
}
